import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../database/firebase";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  Search,
  Flame,
  Droplets,
  Utensils,
  Footprints,
  Moon,
  Plus,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  updateMonthlySummary,
  updateOverallHistory,
  getCurrentMonth
} from "../../utils/wellnessHelpers";

const UserHome = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [activeTab, setActiveTab] = useState("Today");
  const [wellnessData, setWellnessData] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [goals, setGoals] = useState({
    calories: { daily: 2000 },
    water: { daily: 8 },
    meals: { daily: 3 },
    steps: { daily: 10000 },
    sleep: { daily: 8 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [userPurpose, setUserPurpose] = useState(null);

  // Modal state
  const [showMealModal, setShowMealModal] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [mealData, setMealData] = useState({
    name: "",
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
  });

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  useEffect(() => {
    if (user?.uid) {
      loadWellnessData();
    }
  }, [user]);

  /**
   * Load wellness data from Firestore
   */
  const loadWellnessData = async () => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (userDoc.exists()) {
        const data = userDoc.data();
        setWellnessData(data.wellnessProfile);

        // Load goals
        if (data.healthInfo?.goals) {
          setGoals(prev => ({
            ...prev,
            ...data.healthInfo.goals
          }));
        }

        // Load user purpose
        if (data.purpose) {
          setUserPurpose(data.purpose);
        }

        // Load today's data
        const today = new Date().toISOString().split("T")[0];
        const todayRecord = data.healthInfo?.dailyTracking?.records?.find(
          (r) => r.date === today
        );

        setTodayData(todayRecord || {
          date: today,
          calories: { intake: 0, burned: 0 },
          macros: { protein: 0, carbs: 0, fat: 0 },
          meals: [],
          waterGlasses: 0,
          steps: 0,
          sleep: 0,
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error("Error loading wellness data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // CALCULATIONS
  // ============================================================================

  // Calories
  const targetCalories = goals.calories.daily;
  const consumedCalories = todayData?.calories?.intake || 0;
  const remainingCalories = Math.max(0, targetCalories - consumedCalories);
  const caloriesPercentage = Math.min(100, (consumedCalories / targetCalories) * 100);

  // Macros (from today's data)
  const proteinGrams = todayData?.macros?.protein || 0;
  const carbsGrams = todayData?.macros?.carbs || 0;
  const fatGrams = todayData?.macros?.fat || 0;

  // Steps
  const steps = todayData?.steps || 0;
  const stepsGoal = goals.steps.daily;
  const stepsPercentage = (steps / stepsGoal) * 100;

  // Sleep
  const sleepHours = todayData?.sleep || 0;
  const sleepGoal = goals.sleep.daily;
  const sleepPercentage = (sleepHours / sleepGoal) * 100;
  const sleepDisplay = `${Math.floor(sleepHours)}h ${Math.round((sleepHours % 1) * 60)}m`;

  // Meals
  const mealsLogged = todayData?.meals?.length || 0;
  const mealsGoal = goals.meals.daily;
  const mealsPercentage = (mealsLogged / mealsGoal) * 100;

  // Water
  const waterGlasses = todayData?.waterGlasses || 0;
  const waterGoal = goals.water.daily;
  const waterPercentage = (waterGlasses / waterGoal) * 100;

  // ============================================================================
  // MEAL OPERATIONS
  // ============================================================================

  /**
   * Open meal modal
   */
  const handleOpenMealModal = (mealType) => {
    setSelectedMeal(mealType);
    setMealData({
      name: "Kiwi Smoothie Bowl with Granola",
      calories: 450,
      protein: 20,
      carbs: 140,
      fat: 12,
    });
    setShowMealModal(true);
  };

  /**
   * Update meal data field
   */
  const handleMealDataUpdate = (field, value) => {
    setMealData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Save meal to Firestore with monthly and historical updates
   */
  const handleSaveMeal = async () => {
    if (!user?.uid) return;

    try {
      setSaving(true);
      const today = new Date().toISOString().split("T")[0];
      const currentMonth = getCurrentMonth();
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingRecords = userData.healthInfo?.dailyTracking?.records || [];
      const todayIndex = existingRecords.findIndex((r) => r.date === today);

      // Create meal object
      const newMeal = {
        type: selectedMeal.toLowerCase(),
        name: mealData.name,
        calories: mealData.calories,
        macros: {
          protein: mealData.protein,
          carbs: mealData.carbs,
          fat: mealData.fat
        },
        time: new Date().toISOString()
      };

      let updatedRecords;
      if (todayIndex >= 0) {
        // Update existing record
        updatedRecords = [...existingRecords];
        const currentMeals = updatedRecords[todayIndex].meals || [];
        const currentCalories = updatedRecords[todayIndex].calories || { intake: 0, burned: 0 };
        const currentMacros = updatedRecords[todayIndex].macros || { protein: 0, carbs: 0, fat: 0 };

        updatedRecords[todayIndex] = {
          ...updatedRecords[todayIndex],
          meals: [...currentMeals, newMeal],
          calories: {
            ...currentCalories,
            intake: currentCalories.intake + mealData.calories
          },
          macros: {
            protein: currentMacros.protein + mealData.protein,
            carbs: currentMacros.carbs + mealData.carbs,
            fat: currentMacros.fat + mealData.fat
          },
          updatedAt: new Date().toISOString(),
        };
      } else {
        // Create new record
        updatedRecords = [
          ...existingRecords,
          {
            date: today,
            meals: [newMeal],
            calories: { intake: mealData.calories, burned: 0 },
            macros: {
              protein: mealData.protein,
              carbs: mealData.carbs,
              fat: mealData.fat
            },
            waterGlasses: 0,
            waterMl: 0,
            steps: 0,
            sleep: 0,
            bathroomVisits: 0,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      // Calculate monthly summary
      const monthlySummary = updateMonthlySummary(currentMonth, updatedRecords, goals);

      // Calculate overall history
      const overallHistory = updateOverallHistory(updatedRecords);

      // Prepare update object
      const updateObject = {
        "healthInfo.dailyTracking.records": updatedRecords,
        "healthInfo.dailyTracking.lastTrackedDate": today,
        updatedAt: new Date().toISOString(),
      };

      // Add monthly summary
      if (monthlySummary) {
        updateObject[`healthInfo.monthlySummaries.${currentMonth}`] = monthlySummary;
      }

      // Add overall history
      updateObject["healthInfo.overallHistory"] = overallHistory;

      await updateDoc(userRef, updateObject);

      // Update local state
      const updatedTodayData = updatedRecords.find(r => r.date === today);
      setTodayData(updatedTodayData);

      toast.success(`${selectedMeal} added successfully!`);
      setShowMealModal(false);
    } catch (error) {
      console.error("Error saving meal:", error);
      toast.error("Failed to save meal");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Get meal data for display
   */
  const getMealData = (mealType) => {
    const meal = todayData?.meals?.find(m => m.type === mealType.toLowerCase());
    if (!meal) {
      return {
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0
      };
    }
    return {
      calories: meal.calories || 0,
      protein: meal.macros?.protein || 0,
      carbs: meal.macros?.carbs || 0,
      fat: meal.macros?.fat || 0
    };
  };

  // ============================================================================
  // COMPONENTS
  // ============================================================================

  /**
   * Circular Progress Component
   */
  const CircularProgress = ({ percentage, size = 200, strokeWidth = 20 }) => {
    const segments = 20;
    const segmentAngle = 360 / segments;
    const activeSegments = Math.ceil((percentage / 100) * segments);

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {Array.from({ length: segments }).map((_, i) => {
            const angle = i * segmentAngle;
            const isVisible = i >= 5 && i <= 15;
            if (!isVisible) return null;

            const isActive = i < activeSegments;
            return (
              <line
                key={i}
                x1={size / 2}
                y1={strokeWidth / 2}
                x2={size / 2}
                y2={strokeWidth * 1.5}
                stroke={isActive ? "#60A5FA" : "#E5E7EB"}
                strokeWidth={4}
                strokeLinecap="round"
                transform={`rotate(${angle} ${size / 2} ${size / 2})`}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Flame className="w-8 h-8 text-yellow-500 mb-1" />
          <div className="text-5xl font-bold text-gray-900">{remainingCalories}</div>
          <div className="text-sm text-gray-500">Remaining</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  const breakfastData = getMealData("Breakfast");
  const lunchData = getMealData("Lunch");
  const dinnerData = getMealData("Dinner");

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white px-4 pt-3 pb-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <button className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-white rounded-lg shadow-sm">
            <div className="w-5 h-5 grid grid-cols-2 gap-0.5">
              <div className="bg-blue-400 rounded-sm"></div>
              <div className="bg-purple-400 rounded-sm"></div>
              <div className="bg-green-400 rounded-sm"></div>
              <div className="bg-yellow-400 rounded-sm"></div>
            </div>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4">
          {["Today", "Weekly", "Monthly"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeTab === tab
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-600 border border-gray-200"
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Calories Section */}
        <div className="bg-white rounded-3xl p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Calories</h2>
            <button className="text-sm text-gray-600 font-medium">Edit</button>
          </div>

          <div className="flex justify-center mb-6">
            <CircularProgress percentage={caloriesPercentage} />
          </div>

          {/* Macro Indicators */}
          <div className="flex items-center justify-center gap-8">
            <div className="flex items-center gap-2">
              <span className="text-xl">🍩</span>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Protein</span>
                <span className="text-sm font-semibold text-gray-900">{proteinGrams}g</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🍴</span>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Carbs</span>
                <span className="text-sm font-semibold text-gray-900">{carbsGrams}g</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🥦</span>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Fat</span>
                <span className="text-sm font-semibold text-gray-900">{fatGrams}g</span>
              </div>
            </div>
          </div>
        </div>



        {/* Detailed Purpose Section - Highlighted */}
        {userPurpose && (
          <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-5 mb-4 border-2 border-blue-200 shadow-md">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600">Your Goal</h3>
                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  {userPurpose}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Stay focused on your health journey! 🎯
            </p>
          </div>
        )}

        {/* Daily Meals Section */}
        <div className="bg-white rounded-3xl p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">Daily meals</h2>
              <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-xs text-gray-500">ⓘ</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <button className="px-4 py-1.5 bg-gray-900 text-white rounded-full text-sm font-medium">
              Meals
            </button>
            <button className="px-4 py-1.5 bg-white text-gray-600 border border-gray-200 rounded-full text-sm font-medium">
              Activity
            </button>
            <button className="px-4 py-1.5 bg-white text-gray-600 border border-gray-200 rounded-full text-sm font-medium">
              Water
            </button>
          </div>

          {/* Meal Cards */}
          <div className="space-y-3">
            {/* Breakfast */}
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-0.5">Breakfast</h3>
                  <p className="text-sm text-gray-500">{breakfastData.calories} kcal</p>
                </div>
                <button
                  onClick={() => handleOpenMealModal("Breakfast")}
                  className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <span className="text-white text-xl leading-none">+</span>
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                  <span className="text-gray-600">Protein <span className="font-semibold text-gray-900">{breakfastData.protein}g</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                  <span className="text-gray-600">Carbs <span className="font-semibold text-gray-900">{breakfastData.carbs}g</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  <span className="text-gray-600">Fat <span className="font-semibold text-gray-900">{breakfastData.fat}g</span></span>
                </div>
              </div>
            </div>

            {/* Lunch */}
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-0.5">Lunch</h3>
                  <p className="text-sm text-gray-500">{lunchData.calories} kcal</p>
                </div>
                <button
                  onClick={() => handleOpenMealModal("Lunch")}
                  className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <span className="text-white text-xl leading-none">+</span>
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                  <span className="text-gray-600">Protein <span className="font-semibold text-gray-900">{lunchData.protein}g</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                  <span className="text-gray-600">Carbs <span className="font-semibold text-gray-900">{lunchData.carbs}g</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  <span className="text-gray-600">Fat <span className="font-semibold text-gray-900">{lunchData.fat}g</span></span>
                </div>
              </div>
            </div>

            {/* Dinner */}
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-0.5">Dinner</h3>
                  <p className="text-sm text-gray-500">{dinnerData.calories} kcal</p>
                </div>
                <button
                  onClick={() => handleOpenMealModal("Dinner")}
                  className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <span className="text-white text-xl leading-none">+</span>
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                  <span className="text-gray-600">Protein <span className="font-semibold text-gray-900">{dinnerData.protein}g</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                  <span className="text-gray-600">Carbs <span className="font-semibold text-gray-900">{dinnerData.carbs}g</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  <span className="text-gray-600">Fat <span className="font-semibold text-gray-900">{dinnerData.fat}g</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Walk Steps */}
          <motion.div
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-3xl p-5 cursor-pointer"
            onClick={() => navigate("/daily-tracker")}
          >
            <Footprints className="w-6 h-6 text-yellow-600 mb-3" />
            <p className="text-xs text-gray-600 mb-1">Walk Steps</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">
              {steps.toLocaleString()}
            </p>
            <p className="text-xs text-gray-600">{stepsPercentage.toFixed(0)}% Goal</p>
          </motion.div>

          {/* Sleep */}
          <motion.div
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-3xl p-5 cursor-pointer"
            onClick={() => navigate("/daily-tracker")}
          >
            <Moon className="w-6 h-6 text-purple-600 mb-3" />
            <p className="text-xs text-gray-600 mb-1">Sleep</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">{sleepDisplay}</p>
            <p className="text-xs text-gray-600">{sleepPercentage.toFixed(0)}% Goal</p>
          </motion.div>

          {/* Meals */}
          <motion.div
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-br from-green-100 to-green-200 rounded-3xl p-5 cursor-pointer"
          >
            <Utensils className="w-6 h-6 text-green-600 mb-3" />
            <p className="text-xs text-gray-600 mb-1">Meals</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">{mealsLogged}/{mealsGoal}</p>
            <p className="text-xs text-gray-600">{mealsPercentage.toFixed(0)}% Goal</p>
          </motion.div>

          {/* Water */}
          <motion.div
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-3xl p-5 cursor-pointer"
            onClick={() => navigate("/daily-tracker")}
          >
            <Droplets className="w-6 h-6 text-blue-600 mb-3" />
            <p className="text-xs text-gray-600 mb-1">Water</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">
              {waterGlasses}/{waterGoal}
            </p>
            <p className="text-xs text-gray-600">
              {waterPercentage.toFixed(0)}% Goal
            </p>
          </motion.div>
        </div>
      </div>

      {/* Meal Modal */}
      <AnimatePresence>
        {showMealModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="bg-white rounded-t-3xl w-full max-w-md pb-8"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800')",
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            >
              <div className="bg-gradient-to-b from-white/95 to-white/98 backdrop-blur-md rounded-t-3xl p-6">
                {/* Meal Name */}
                <div className="mb-4">
                  <span className="text-sm text-gray-600">{selectedMeal}</span>
                </div>

                <input
                  type="text"
                  value={mealData.name}
                  onChange={(e) => handleMealDataUpdate("name", e.target.value)}
                  className="text-2xl font-bold text-gray-900 mb-6 w-full bg-transparent border-none outline-none"
                />

                {/* Macro Cards Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {/* Calories */}
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Flame className="w-4 h-4 text-orange-500" />
                        <span className="text-sm text-orange-500 font-medium">Calories</span>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={mealData.calories}
                      onChange={(e) => handleMealDataUpdate("calories", parseInt(e.target.value) || 0)}
                      className="text-3xl font-bold text-gray-900 w-full bg-transparent border-none outline-none"
                    />
                  </div>

                  {/* Protein */}
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        <span className="text-sm text-blue-500 font-medium">Protein</span>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={mealData.protein}
                      onChange={(e) => handleMealDataUpdate("protein", parseInt(e.target.value) || 0)}
                      className="text-3xl font-bold text-gray-900 w-full bg-transparent border-none outline-none"
                    />
                    <span className="text-sm text-gray-600">gm</span>
                  </div>

                  {/* Carbs */}
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🍇</span>
                        <span className="text-sm text-purple-500 font-medium">Carbs</span>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={mealData.carbs}
                      onChange={(e) => handleMealDataUpdate("carbs", parseInt(e.target.value) || 0)}
                      className="text-3xl font-bold text-gray-900 w-full bg-transparent border-none outline-none"
                    />
                    <span className="text-sm text-gray-600">gm</span>
                  </div>

                  {/* Fat */}
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🥑</span>
                        <span className="text-sm text-green-500 font-medium">Fat</span>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={mealData.fat}
                      onChange={(e) => handleMealDataUpdate("fat", parseInt(e.target.value) || 0)}
                      className="text-3xl font-bold text-gray-900 w-full bg-transparent border-none outline-none"
                    />
                    <span className="text-sm text-gray-600">gm</span>
                  </div>
                </div>

                {/* Done Button */}
                <button
                  onClick={handleSaveMeal}
                  disabled={saving}
                  className="w-full bg-gray-900 text-white py-4 rounded-2xl font-semibold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Done"}
                </button>

                {/* Bottom Indicator */}
                <div className="flex justify-center mt-4">
                  <div className="w-32 h-1 bg-gray-300 rounded-full"></div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserHome;